/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import {
  Autocomplete,
  Box,
  Grid,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { useGetAutomationRuns } from "../../api/automationRuns";
import { useGetEnvs } from "../../api/envs";
import DataGridButton from "../../components/DataGridButton";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import TimeCell from "../../components/TimeCell";
import { dataGridButtonTypography } from "../../styles/DataGridStyles";
import { dataGridButtonStyles } from "../../styles/buttonStyles";
import { tabbedDataGridPage } from "../../styles/commonPageStyles";
import { INFINITE_SCROLLING_PAGE_SIZE } from "../../utils/constants";
import { adaptAutomationRuns, getDeploymentStage } from "./utils";
import { qaosUrl } from "../../utils/urls";
import { useReportModal } from "../../components/ReportModal";
import useTriggerAutomationDialog from "./TriggerAutomationDialog";
import {
  downloadFileFromUrl,
  showNotification,
} from "../../utils/helperFunctions";
import Notification from "../../components/Notification";
import { FileDownload, Sync } from "@mui/icons-material";
import ResultBox from "./ResultBox";

const styles = {
  // Column layout so the grid takes every pixel left under the header row.
  mainBox: {
    ...tabbedDataGridPage.tabContentMainBox,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  headerGrid: { ...tabbedDataGridPage.gridContainer, flexShrink: 0 },
  dataTable: { flex: 1, minHeight: 0 },
  envSelect: { minWidth: "220px" },
};

const AutomationRuns = () => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);

  const { tenantUrl } = useParams();

  // The scoped route (/tenants/:tenantUrl/automation-runs) only seeds the
  // initial selection; switching environments afterwards is page-local state.
  const [selectedTenantUrl, setSelectedTenantUrl] = useState(tenantUrl);

  const [automationRuns, setAutomationRuns] = useState({ page: 0, runs: [] });
  const [currPage, setCurrPage] = useState(1);

  const { reportModal, setReportModalOpen } = useReportModal();

  const [notify, setNotify] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("");

  const notificationStates = {
    setNotify: setNotify,
    setMessage: setMessage,
    setSeverity: setSeverity,
  };

  const showNotificationWrapper = (resp, respStatus, callback = () => {}) => {
    showNotification(
      resp,
      respStatus,
      callback,
      notificationStates.setMessage,
      notificationStates.setSeverity,
      notificationStates.setNotify
    );
  };

  const {
    postData: getEnvs,
    response: getEnvsResp,
    isLoading: isLoadingEnvs,
  } = useGetEnvs();

  useEffect(() => {
    getEnvs();
  }, []);

  // Only dev/qa envs qualify: the env's domain decides which QAOS backend holds
  // its runs (see getDeploymentStage); stage/prod tenants have no data here.
  const envOptions = useMemo(
    () =>
      (getEnvsResp?.response || [])
        .map((env) => ({
          name: env?.name,
          url: env?.meta?.tenant_api_endpoint?.replace("api.", ""),
        }))
        .filter(
          (env) =>
            env.url && ["dev", "qa"].includes(getDeploymentStage(env.url))
        )
        .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [getEnvsResp]
  );

  // Unscoped route: default to the first env once the list arrives.
  useEffect(() => {
    if (!selectedTenantUrl && envOptions.length) {
      setSelectedTenantUrl(envOptions[0].url);
    }
  }, [envOptions]);

  const {
    postData: listAllAutomationRuns,
    response: listAllAutomationRunsResp,
    isLoading: isLoadingAutomationRuns,
  } = useGetAutomationRuns(
    getDeploymentStage(selectedTenantUrl),
    selectedTenantUrl
  );

  const listAllAutomationRunsWrapper = (pageNo) => {
    listAllAutomationRuns(
      `?pageSize=${INFINITE_SCROLLING_PAGE_SIZE}&page=${pageNo}`
    );
  };

  const { triggerAutomationDialog, setTriggerAutomationDialogOpen } =
    useTriggerAutomationDialog(
      getDeploymentStage(selectedTenantUrl),
      selectedTenantUrl,
      showNotificationWrapper,
      () => {
        setAutomationRuns({ page: 0, runs: [] });
        if (currPage !== 1) {
          setCurrPage(1);
        } else {
          listAllAutomationRunsWrapper(1);
        }
      }
    );

  // Wait for the tenant-instances call to resolve before requesting runs — on
  // the scoped route (/tenants/:tenantUrl/automation-runs) selectedTenantUrl
  // is already set at mount, so without this the two calls fire together.
  useEffect(() => {
    if (selectedTenantUrl && getEnvsResp) {
      listAllAutomationRunsWrapper(currPage);
    }
  }, [currPage, selectedTenantUrl, getEnvsResp]);

  const handleEnvChange = (url) => {
    if (!url || url === selectedTenantUrl) return;
    setAutomationRuns({ page: 0, runs: [] });
    setSelectedTenantUrl(url);
    // A currPage change refires the fetch effect; when already on page 1 the
    // selectedTenantUrl change does it instead.
    if (currPage !== 1) {
      setCurrPage(1);
    }
  };

  const selectedEnvName =
    envOptions.find((env) => env.url === selectedTenantUrl)?.name ??
    selectedTenantUrl?.split(".")[0];

  const handleShowReport = (id) => {
    const depStage = getDeploymentStage(selectedTenantUrl);
    setReportModalOpen(
      `${qaosUrl}/tessell/${depStage}/tenants/${selectedTenantUrl}/automation-runs/${id}/report`
    );
  };

  useEffect(() => {
    if (Array.isArray(listAllAutomationRunsResp?.automationRuns)) {
      setAutomationRuns((prev) => {
        if (currPage !== prev?.page + 1) {
          return prev;
        }
        const newRuns = adaptAutomationRuns(
          listAllAutomationRunsResp?.automationRuns
        );
        return {
          page: prev?.page + 1,
          runs: prev?.page ? [...(prev?.runs || []), ...newRuns] : newRuns,
        };
      });
    }
  }, [listAllAutomationRunsResp]);

  const columns = [
    {
      field: "workflowSuffix",
      flex: 1,
      renderHeader: () => "Workflow",
      cellClassName: "name-column--cell",
      renderCell: ({ row: { workflowSuffix } }) => workflowSuffix ?? "",
    },
    {
      field: "status",
      flex: 1.2,
      renderHeader: () => "Status",
      renderCell: ({ row: { status } }) => <StatusChip value={status} />,
    },
    {
      field: "result",
      flex: 2,
      renderHeader: () => "Result",
      renderCell: ({ row: { compliedResults } }) => (
        <ResultBox compliedResults={compliedResults} />
      ),
    },
    {
      field: "startTime",
      flex: 1.3,
      minWidth: 118,
      renderHeader: () => "Started",
      renderCell: ({ row: { startTime } }) => <TimeCell value={startTime} />,
    },
    {
      field: "endTime",
      flex: 1.3,
      minWidth: 118,
      renderHeader: () => "Ended",
      renderCell: ({ row: { endTime } }) => <TimeCell value={endTime} />,
    },
    {
      field: "duration",
      flex: 1,
      renderHeader: () => "Duration",
      renderCell: ({ row: { duration } }) => duration ?? "",
    },
    {
      field: "submittedBy",
      flex: 1.5,
      renderHeader: () => "Submitted By",
      renderCell: ({ row: { submittedBy } }) =>
        submittedBy?.split("@")?.[0] ?? "",
    },
    {
      field: "report",
      flex: 1.2,
      renderHeader: () => "Report",
      headerAlign: "center",
      renderCell: ({ row: { id, status } }) => (
        <DataGridButton
          sx={dataGridButtonStyles.refreshButton}
          onClick={() => handleShowReport(id)}
          disabled={status !== "COMPLETED"}
        >
          <Typography sx={dataGridButtonTypography(colors)}>
            View Report
          </Typography>
        </DataGridButton>
      ),
    },
    {
      field: "actions",
      type: "actions",
      flex: 0.5,
      getActions: ({ row }) =>
        [
          row?.status === "COMPLETED" && (
            <GridActionsCellItem
              label="Download Results"
              icon={
                <Tooltip title="Download Results">
                  <FileDownload />
                </Tooltip>
              }
              onClick={() => {
                downloadFileFromUrl(
                  `${qaosUrl}/tessell/${getDeploymentStage(
                    selectedTenantUrl
                  )}/tenants/${selectedTenantUrl}/automation-runs/${
                    row?.id
                  }/results`
                );
              }}
            />
          ),
        ].filter(Boolean),
    },
  ];
  return (
    <Box sx={styles.mainBox}>
      {reportModal}
      {triggerAutomationDialog}
      <Notification
        notify={notify}
        severity={severity}
        setNotify={setNotify}
        message={message}
      />
      <Grid sx={styles.headerGrid} container columns={2}>
        <Grid item xs={1}>
          <Header
            title="Automation Runs"
            subtitle={
              selectedEnvName
                ? `View the automation runs for ${selectedEnvName}`
                : "Select an environment to view its automation runs"
            }
          />
        </Grid>
        <Grid item xs={1}>
          <Stack direction={"row-reverse"} spacing={1} alignItems="center">
            <DataGridButton
              sx={dataGridButtonStyles.pageTopButtons}
              onClick={() => {
                setAutomationRuns({ page: 0, runs: [] });
                if (currPage !== 1) {
                  setCurrPage(1);
                } else {
                  listAllAutomationRunsWrapper(1);
                }
              }}
            >
              <Sync sx={{ fontSize: 16, color: "#fff" }} />
              <Typography sx={dataGridButtonTypography(colors, 500)}>
                RELOAD
              </Typography>
            </DataGridButton>
            <DataGridButton
              sx={dataGridButtonStyles.pageTopButtons}
              onClick={setTriggerAutomationDialogOpen}
            >
              <Typography sx={dataGridButtonTypography(colors, 500)}>
                SUBMIT NEW
              </Typography>
            </DataGridButton>
            <Autocomplete
              size="small"
              sx={styles.envSelect}
              options={envOptions}
              getOptionLabel={(env) => env.name || ""}
              isOptionEqualToValue={(option, value) => option.url === value.url}
              value={
                envOptions.find((env) => env.url === selectedTenantUrl) ?? null
              }
              onChange={(_, env) => handleEnvChange(env?.url)}
              disableClearable
              autoHighlight
              componentsProps={{
                popper: {
                  style: {
                    width: "fit-content",
                    minWidth: "220px",
                    maxWidth: "480px",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField {...params} label="Environment" />
              )}
            />
          </Stack>
        </Grid>
      </Grid>
      <DataTable
        height="100%"
        sx={styles.dataTable}
        onRowsScrollEnd={() => {
          setCurrPage(automationRuns?.page + 1);
        }}
        scrollEndThreshold={1}
        loading={isLoadingEnvs || isLoadingAutomationRuns}
        rows={automationRuns?.runs}
        columns={columns}
      />
    </Box>
  );
};

export default AutomationRuns;
